// Results modal for a BULK action (engine action ``run_action_bulk``). After
// the action runs against every selected record, this shows a one-line summary
// (how many succeeded / were not eligible / were skipped / failed) and a table
// with each record's outcome. Mirrors the portal + z-index of the other action
// presenters so it opens cleanly from inside panels that form their own
// stacking contexts (the list toolbar / per-row menu live deep in the tree).

import { createPortal } from 'react-dom'
import type { BulkActionRowResult, BulkActionSummary } from './contracts'
import { tw } from '../testHooks'

interface Props {
  title?: string
  results: BulkActionRowResult[]
  summary?: BulkActionSummary
  // Optional pretty label per record id (e.g. a name column) for the first
  // table cell — falls back to the raw txn_id.
  labelFor?: (txnId: string) => string
  onClose: () => void
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 12000,
}

// Visual treatment per outcome — colour + short human label.
const STATUS_META: Record<BulkActionRowResult['status'], { label: string; fg: string; bg: string }> = {
  success: { label: 'Done', fg: '#0a7d4f', bg: 'rgba(16,185,129,0.14)' },
  ineligible: { label: 'Not eligible', fg: '#92400e', bg: 'rgba(245,158,11,0.16)' },
  skipped: { label: 'Skipped', fg: '#92400e', bg: 'rgba(234,179,8,0.16)' },
  failed: { label: 'Failed', fg: '#b91c1c', bg: 'rgba(239,68,68,0.14)' },
}

function StatusPill({ status }: { status: BulkActionRowResult['status'] }) {
  const m = STATUS_META[status]
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 999,
      fontSize: '0.8125rem', fontWeight: 700, color: m.fg, background: m.bg,
      whiteSpace: 'nowrap',
    }}>{m.label}</span>
  )
}

export default function BulkResultModal({ title, results, summary, labelFor, onClose }: Props) {
  if (typeof document === 'undefined') return null // SSR safety
  const counts = summary || {
    total: results.length,
    success: results.filter(r => r.status === 'success').length,
    ineligible: results.filter(r => r.status === 'ineligible').length,
    skipped: results.filter(r => r.status === 'skipped').length,
    failed: results.filter(r => r.status === 'failed').length,
  }
  // Compose the summary line, omitting zero buckets so it stays readable.
  const parts: string[] = []
  if (counts.success) parts.push(`${counts.success} done`)
  if (counts.ineligible) parts.push(`${counts.ineligible} not eligible`)
  if (counts.skipped) parts.push(`${counts.skipped} skipped`)
  if (counts.failed) parts.push(`${counts.failed} failed`)
  const summaryLine = parts.join(' · ') || 'No records processed.'

  return createPortal(
    <div style={overlayStyle} onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        data-tw-dialog="bulk_result"
        style={{
          background: 'var(--color-surface, #fff)', color: 'var(--color-text, #111)',
          borderRadius: 8, width: 'min(680px, 94vw)', maxHeight: '86vh',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
        }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '10px 16px', borderBottom: '1px solid var(--color-border, #e5e5e5)',
        }}>
          <span style={{ fontWeight: 600, fontSize: '0.9625rem' }}>{title || 'Action results'}</span>
          <button
            onClick={onClose}
            {...tw('action', 'close')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1625rem', color: 'var(--color-text-secondary, #666)' }}
            title="Close">×</button>
        </div>

        <div style={{
          padding: '10px 16px', fontSize: '0.8825rem', fontWeight: 600,
          borderBottom: '1px solid var(--color-border, #e5e5e5)',
          color: 'var(--color-text-secondary, #555)',
        }}>
          {counts.total} record{counts.total === 1 ? '' : 's'}: {summaryLine}
        </div>

        <div style={{ overflow: 'auto', minHeight: 0 }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.8625rem' }}>
            <thead>
              <tr>
                <th style={th}>Record</th>
                <th style={{ ...th, width: 110 }}>Result</th>
                <th style={th}>Details</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r, i) => (
                <tr key={`${r.txn_id}-${i}`} style={{ borderTop: '1px solid var(--color-border, #eee)' }}>
                  <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 600 }}>
                    {labelFor ? labelFor(r.txn_id) : r.txn_id}
                  </td>
                  <td style={td}><StatusPill status={r.status} /></td>
                  <td style={{ ...td, color: r.status === 'failed' ? 'var(--color-danger, #b91c1c)' : 'var(--color-text, #111)' }}>
                    {r.message || (r.status === 'success' ? 'Done' : '')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: 8,
          padding: '10px 16px', borderTop: '1px solid var(--color-border, #e5e5e5)',
        }}>
          <button
            onClick={onClose}
            {...tw('action', 'ok')}
            style={{ padding: '6px 16px', fontSize: '0.8625rem', borderRadius: 6, border: 'none', background: 'var(--color-accent, #4c8dff)', color: '#fff', cursor: 'pointer' }}>
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

const th: React.CSSProperties = {
  padding: '8px 12px', textAlign: 'left', fontWeight: 600, fontSize: '0.8125rem',
  color: 'var(--color-text-secondary, #666)', textTransform: 'none',
  background: 'var(--color-surface-2, var(--color-bg-secondary, #fafafa))',
  position: 'sticky', top: 0,
}

const td: React.CSSProperties = {
  padding: '8px 12px', verticalAlign: 'top',
}
